import React, {useCallback, useEffect, useState} from 'react';
import CryptoJS from 'crypto-js';
import {Autocomplete, Grid, TextField} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {Controller, useFormContext, useWatch, useFormState, useForm} from "react-hook-form";

const OperatorData = ({legalEntities, dealers, mobilityStores, logisticCompounds, departments, profiles, state}: any) => {
    const {t} = useTranslation(['operator']);
    const {register, formState: {errors}, setValue, getValues, control, watch} = useFormContext();
    // @ts-ignore
    const isTrustbuilderCreate = state === 'create' && window?.appConfig?.OIDC_PROVIDER === 'TRUSTBUILDER';
    const [filteredDealers, setFilteredDealers] = useState<any[]>([]);
    const [filteredMobilityStores, setFilteredMobilityStores] = useState<any[]>([]);
    const [filteredLogisticCompounds, setFilteredLogisticCompounds] = useState<any[]>([]);
    const [filteredProfiles, setFilteredProfiles] = useState<any[]>([]);

    useEffect(() => {
        if (legalEntities?.length === 1) {
            setFilteredDealers(dealers);
            setFilteredMobilityStores(mobilityStores);
            setFilteredLogisticCompounds(logisticCompounds);
        } else if (state === 'edit') {
            let tmpDealers = dealers?.filter((dealer: any) => {
                if (legalEntities?.length > 0) {
                    return dealer.legalEntities?.some((le: any) =>
                        legalEntities.find((legalEntity: any) => legalEntity.id === le.id)
                    );
                } else {
                    return true;
                }
            });
            setFilteredDealers(tmpDealers);

            let tmpMobilityStores = mobilityStores?.filter((mobilityStore: any) => {
                if (legalEntities?.length > 0) {
                    return legalEntities?.find((legalEntity: any) => legalEntity.name === mobilityStore.tenant);
                } else {
                    return null;
                }
            });
            setFilteredMobilityStores(tmpMobilityStores);

            let tmpLogisticCompounds = logisticCompounds?.filter((logisticCompound: any) => {
                if (legalEntities?.length > 0) {
                    return legalEntities?.find((legalEntity: any) => legalEntity.name === logisticCompound.tenant);
                } else {
                    return null;
                }
            });
            setFilteredLogisticCompounds(tmpLogisticCompounds);
        }

        let tmpProfiles = profiles?.filter((profile: any) => {
            if (departments?.length > 0) {
                return departments?.find((department: any) => department.id === profile.department.id);
            } else {
                return null;
            }
        }).filter((profile: any) => {
            if (getValues('departments') && getValues('departments').length > 0) {
                return getValues('departments').find((department: any) => department.id === profile.department.id);
            } else {
                return null;
            }
        });
        setFilteredProfiles(tmpProfiles);
    }, [legalEntities, dealers, mobilityStores, logisticCompounds, departments, profiles]);

    useEffect(() => {
        if (isTrustbuilderCreate) {
            const generateUsername = (email: string) => {
                const timestamp = Date.now().toString();
                const raw = `${email}-${timestamp}`;
                return CryptoJS.SHA1(raw).toString().substring(0, 10).toUpperCase();
            };

            // Generazione iniziale
            const initialEmail = getValues('email') || '';
            setValue('username', generateUsername(initialEmail));

            // Watch per aggiornamenti email
            const emailWatcher = watch((value, { name }) => {
                if (name === 'email') {
                    const email = value.email || '';
                    setValue('username', generateUsername(email));
                }
            });

            return () => emailWatcher.unsubscribe();
        }
    }, [isTrustbuilderCreate]);

    const handleChangeLegalEntities = useCallback((event:any, value:any) => {
        let tmpDealers = dealers?.filter((dealer: any) => {
            if (value?.length > 0) {
                return dealer.legalEntities?.some((le: any) =>
                    value.find((legalEntity: any) => legalEntity.id === le.id)
                );
            } else {
                return true;
            }
        });
        setFilteredDealers(tmpDealers);

        let tmpMobilityStores = mobilityStores?.filter((mobilityStore: any) => {
            if (value?.length > 0) {
                return value?.find((legalEntity: any) => legalEntity.name === mobilityStore.tenant);
            } else {
                return null;
            }
        });
        setFilteredMobilityStores(tmpMobilityStores);

        let tmpLogisticCompounds = logisticCompounds?.filter((logisticCompound: any) => {
            if (value?.length > 0) {
                return value?.find((legalEntity: any) => legalEntity.name === logisticCompound.tenant);
            } else {
                return null;
            }
        });
        setFilteredLogisticCompounds(tmpLogisticCompounds);

        setValue('dealers', getValues('dealers') || []);
        setValue('mobilityStores', getValues('mobilityStores') || []);
        setValue('logisticCompounds', getValues('logisticCompounds') || []);
    }, [])

    return (
        <>
            {legalEntities.length > 1 &&
                <Grid item xs={12} sm={3}>
                    <Controller
                        control={control}
                        name="legalEntities"
                        render={({field: {onChange, value}}: any) => (
                            <Autocomplete
                                limitTags={1}
                                disableCloseOnSelect={true}
                                blurOnSelect={false}
                                multiple
                                onChange={(event, value) => {
                                    handleChangeLegalEntities(event, value);
                                    onChange(value);
                                }}
                                value={value || []}
                                options={legalEntities}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                getOptionLabel={(option: any) => t('translation:legalEntities.'.concat(option.name))}
                                renderInput={(params) => (
                                    <TextField {...params}
                                               label={t("label.legalEntity")}
                                               error={Boolean(errors.legalEntities)}
                                               helperText={t(errors.legalEntities?.message as string)}
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>
            }
            {dealers?.length > 1 &&
                <Grid item xs={12} sm={3}>
                    <Controller
                        control={control}
                        name="dealers"
                        render={({field: {onChange, value}}: any) => (
                            <Autocomplete
                                limitTags={1}
                                disableCloseOnSelect={true}
                                blurOnSelect={false}
                                multiple
                                onChange={(event, value) => {
                                    onChange(value);
                                }}
                                value={value || []}
                                options={filteredDealers}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                getOptionLabel={(option: any) => t('translation:dealers.'.concat(option.name))}
                                renderInput={(params) => (
                                    <TextField {...params}
                                               label={t("label.dealer")}
                                               error={Boolean(errors.dealers)}
                                               helperText={t(errors.dealers?.message as string)}
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>
            }
            {mobilityStores?.length > 1 &&
                <Grid item xs={12} sm={3}>
                    <Controller
                        control={control}
                        name="mobilityStores"
                        render={({field: {onChange, value}}: any) => (
                            <Autocomplete
                                limitTags={1}
                                disableCloseOnSelect={true}
                                blurOnSelect={false}
                                multiple
                                onChange={(event, value) => {
                                    if (value?.length > 0 && value?.find((logisticCompound: any) => logisticCompound.id === -1)) {
                                        onChange([{id: 0, name: t('label.selectAll')}]);
                                    } else {
                                        onChange(value);
                                    }
                                }}
                                value={value || []}
                                options={[{id:-1, name: t('label.selectAll')}, ...filteredMobilityStores]}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                getOptionLabel={(option: any) => option.name}
                                renderInput={(params) => (
                                    <TextField {...params}
                                               label={t("label.mobilityStore")}
                                               error={Boolean(errors.mobilityStores)}
                                               helperText={t(errors.mobilityStores?.message as string)}
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>
            }
            {logisticCompounds?.length > 1 &&
                <Grid item xs={12} sm={3}>
                    <Controller
                        control={control}
                        name="logisticCompounds"
                        render={({field: {onChange, value}}: any) => (
                            <Autocomplete
                                limitTags={1}
                                disableCloseOnSelect={true}
                                blurOnSelect={false}
                                multiple
                                onChange={(event, value) => {
                                    if (value?.length > 0 && value?.find((logisticCompound: any) => logisticCompound.id === -1)) {
                                        onChange([{id: 0, name: t('label.selectAll')}]);
                                    } else {
                                        onChange(value);
                                    }
                                }}
                                value={value || []}
                                options={[{id:-1, name: t('label.selectAll')}, ...filteredLogisticCompounds]}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                getOptionLabel={(option: any) => option.name}
                                renderInput={(params) => (
                                    <TextField {...params}
                                               label={t("label.logisticCompound")}
                                               error={Boolean(errors.logisticCompounds)}
                                               helperText={t(errors.logisticCompounds?.message as string)}
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>
            }
            <Grid item xs={12} sm={3}>
                <Controller
                    control={control}
                    name="username"
                    render={({field: {onChange, value}}: any) => (
                        <TextField
                            fullWidth
                            id="username"
                            name="username"
                            type="text"
                            label={t('label.username')}
                            error={Boolean(errors.username)}
                            helperText={t(errors.username?.message as string)}
                            value={isTrustbuilderCreate ? getValues('username') : value || ''}
                            onChange={isTrustbuilderCreate ? () => {} : onChange}
                            InputProps={{
                                disabled: isTrustbuilderCreate
                            }}
                        />
                    )}
                />
            </Grid>
            {/*// @ts-ignore*/}
            {!JSON.parse(window?.appConfig?.ADFS_ENABLED.toLowerCase()) &&
                <Grid item xs={12} sm={3}>
                    <Controller
                        control={control}
                        name="password"
                        render={({field: {onChange, value}}: any) => (
                            <TextField
                                fullWidth
                                id="password"
                                name="password"
                                type="text"
                                label={t('label.password')}
                                error={Boolean(errors.password)}
                                helperText={t(errors.password?.message as string)}
                                value={value || ''}
                                onChange={onChange}
                            />
                        )}
                    />
                </Grid>
            }
            <Grid item xs={12} sm={3}>
                <Controller
                    control={control}
                    name="name"
                    render={({field: {onChange, value}}: any) => (
                        <TextField
                            fullWidth
                            id="name"
                            name="name"
                            type="text"
                            label={t('label.name')}
                            error={Boolean(errors.name)}
                            helperText={t(errors.name?.message as string)}
                            value={value || ''}
                            onChange={onChange}
                        />
                    )}
                />
            </Grid>
            <Grid item xs={12} sm={3}>
                <Controller
                    control={control}
                    name="surname"
                    render={({field: {onChange, value}}: any) => (
                        <TextField
                            fullWidth
                            id="surname"
                            name="surname"
                            type="text"
                            label={t('label.surname')}
                            error={Boolean(errors.surname)}
                            helperText={t(errors.surname?.message as string)}
                            value={value || ''}
                            onChange={onChange}
                        />
                    )}
                />
            </Grid>
            <Grid item xs={12} sm={3}>
                <Controller
                    control={control}
                    name="email"
                    render={({field: {onChange, value}}: any) => (
                        <TextField
                            fullWidth
                            id="email"
                            name="email"
                            type="text"
                            label={t('label.email')}
                            error={Boolean(errors.email)}
                            helperText={t(errors.email?.message as string)}
                            value={value || ''}
                            onChange={onChange}
                        />
                    )}
                />
            </Grid>
            <Grid item xs={12} sm={3}>
                <Controller
                    control={control}
                    name="departments"
                    render={({field: {onChange, value}}: any) => (
                        <Autocomplete
                            limitTags={1}
                            disableCloseOnSelect={true}
                            blurOnSelect={false}
                            multiple
                            onChange={(event, value) => {
                                let tmpProfiles = profiles?.filter((profile: any) => {
                                    if (value?.length > 0) {
                                        return value?.find((department: any) => department.id === profile.department?.id);
                                    } else {
                                        return null;
                                    }
                                });
                                setFilteredProfiles(tmpProfiles);
                                setValue('profiles', getValues('profiles') || []);
                                onChange(value);
                            }}
                            value={value || []}
                            options={departments}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={(option: any) => t('translation:departments.'.concat(option.name))}
                            renderInput={(params) => (
                                <TextField {...params}
                                           label={t("label.department")}
                                           error={Boolean(errors.departments)}
                                           helperText={t(errors.departments?.message as string)}
                                />
                            )}
                        />
                    )}
                />
            </Grid>
            <Grid item xs={12} sm={3}>
                <Controller
                    control={control}
                    name="profiles"
                    render={({field: {onChange, value}}: any) => (
                        <Autocomplete
                            limitTags={1}
                            disableCloseOnSelect={true}
                            blurOnSelect={false}
                            multiple
                            onChange={(event, value) => {
                                onChange(value);
                            }}
                            value={value || []}
                            options={filteredProfiles}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={(option: any) => option.name}
                            renderInput={(params) => (
                                <TextField {...params}
                                           label={t("label.profile")}
                                           error={Boolean(errors.profiles)}
                                           helperText={t(errors.profiles?.message as string)}
                                />
                            )}
                        />
                    )}
                />
            </Grid>
        </>
    );
};

export default OperatorData;
